<template>
  <SideSheet
    ref="sheet"
    :is-open="props.isOpen"
    position="right"
    @close="handleSheetClose"
  >
    <div class="sheet" data-testid="sheet">
      <div class="sheet__header">
        <ButtonIconContextual
          class="sheet__close"
          data-testid="sheet__close"
          icon="EF0030"
          label="Fechar"
          size="small"
          @click="emit('close')"
        />
      </div>

      <div class="sheet__content">
        <Heading :level="3">Limite Day Trade</Heading>
        <BaseText class="sheet__content__subtitle" size="p4"
          >Visualize seu limite de operações e risco diário.</BaseText
        >

        <ContentDisplay
          class="sheet__content__content-display"
          :blocks-length="2"
        >
          <template #content-block-1>
            <ListDisplay
              v-for="item in marginAllocation?.dailyLoss.rows"
              :key="item.label"
              :title="item.label"
            >
              <template #value-slot>
                <Money width="70px">{{ item.value }}</Money>
              </template>
            </ListDisplay>
          </template>
          <template #content-block-2>
            <ListDisplay
              v-for="item in marginAllocation?.availableBalance.rows"
              :key="item.label"
              :title="item.label"
            >
              <template #value-slot>
                <Money width="70px">{{ item.value }}</Money>
              </template>
            </ListDisplay>
          </template>
        </ContentDisplay>
      </div>

      <div class="sheet__footer">
        <ButtonPrimary
          size="large"
          label="Editar limite"
          @click="handleEditLimit"
        />
      </div>
    </div>
  </SideSheet>
</template>

<script setup lang="ts">
import { ref, withDefaults } from 'vue';
import { SideSheet } from '@/modules/common/components/SideSheet';
import {
  ButtonIconContextual,
  BaseText,
  Heading,
  ContentDisplay,
  ListDisplay,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { IMarginAllocationV2 } from '@/modules/trade/services/margin-allocation';
import { Money } from '@/modules/common/components/MoneyV2';
import { useRouter } from 'vue-router/composables';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';

const router = useRouter();

const sheet = ref<HTMLElement | null>(null);

interface Props {
  isOpen: boolean;
  marginAllocation?: IMarginAllocationV2;
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  marginAllocation: () => ({}) as IMarginAllocationV2
});

const emit = defineEmits(['close']);

const handleSheetClose = () => {
  emit('close');
};

const handleEditLimit = () => {
  if (FeatureFlagsModule.isEnabled(FeatureFlags.DISABLE_ELLIOT)) {
    emit('close');
    return;
  }

  router.push({
    name: 'marginAllocation'
  });
};
</script>

<style lang="less" scoped>
.sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  min-height: 100vh;
  background: @background-secondary;

  &__wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: space-between;
    justify-content: space-between;
  }

  &__header,
  &__content,
  &__footer {
    width: 100%;
  }

  &__header {
    padding: @size-spacing-x400;
  }

  &__content {
    padding: 0 @size-spacing-x1200;

    &__title {
      padding-bottom: @size-spacing-x800;
    }

    &__subtitle {
      padding-top: @size-spacing-x400;
    }

    &__content-display {
      margin-top: @size-spacing-x800;
    }
  }

  &__footer {
    border-top: @size-border-x400 solid @divider-primary;
    padding: @size-spacing-x400;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

:deep(.panel-content.right) {
  width: 100%;
  @media screen and (min-width: 768px) {
    width: 683px;
  }
}

:deep(.container-right) {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

:deep(.row-item > div) {
  padding-top: @size-spacing-x500;
}

:deep(.content-display__block:nth-child(2)) {
  border-top: @size-border-x400 solid @divider-primary;
  padding-top: @size-spacing-x500;
}
</style>
